import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as AIIcon,
  Schedule as ScheduleIcon,
  ShoppingCart as CartIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import styled from "styled-components";
import { colors } from "../../theme";
import { apiService } from "../../modules/services";
import { userPreferencesController } from "../../modules/controllers";

const GeneratorContainer = styled(Paper)`
  padding: 32px;
  border-radius: 16px;
  margin: 32px 0;
  background: white;
`;

const StepContainer = styled(Box)`
  margin: 24px 0;
`;

const MealCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }
`;

const GenerateButton = styled(Button)`
  background: ${colors.gradients.primary} !important;
  color: white !important;
  padding: 16px 32px !important;
  font-size: 1.1rem !important;
  font-weight: bold !important;
  border-radius: 12px !important;
  text-transform: none !important;
  font-family: "Montserrat", sans-serif !important;
`;

const steps = [
  "Configure Your Plan",
  "Generate Meal Plan",
  "Review & Customize",
  "Schedule & Save",
];

export const MealPlanGenerator = observer(() => {
  const [activeStep, setActiveStep] = useState(0);
  const [planConfig, setPlanConfig] = useState({
    days: 7,
    mealsPerDay: 3,
    caloriesPerDay: 2000,
    planName: `Meal Plan ${new Date().toLocaleDateString()}`,
  });
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    // Load user preferences for auto-configuration
    userPreferencesController.loadUserPreferences();
  }, []);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const generateMealPlan = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await apiService.generateMealPlan({
        days: planConfig.days,
        meals_per_day: planConfig.mealsPerDay,
        calories_per_day: planConfig.caloriesPerDay,
      });

      setGeneratedPlan(response.data.meal_plan);
      setSuccess("Meal plan generated successfully!");
      handleNext();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to generate meal plan");
    } finally {
      setLoading(false);
    }
  };

  const saveMealPlan = async () => {
    try {
      setLoading(true);
      setError("");

      await apiService.saveMealPlan({
        name: planConfig.planName,
        meals: generatedPlan?.meals || [],
      });

      setSuccess("Meal plan saved successfully!");
      handleNext();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save meal plan");
    } finally {
      setLoading(false);
    }
  };

  const addToGroceryList = async () => {
    try {
      if (!generatedPlan?.meals) return;

      setLoading(true);
      const ingredients = [];

      generatedPlan.meals.forEach((meal) => {
        meal.ingredients?.forEach((ingredient) => {
          if (!ingredients.includes(ingredient)) {
            ingredients.push(ingredient);
          }
        });
      });

      for (const ingredient of ingredients) {
        await apiService.addToGroceryCart({
          item_name: ingredient,
          quantity: 1,
          category: "Meal Plan Ingredients",
        });
      }

      setSuccess("Ingredients added to grocery cart!");
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to add ingredients to cart"
      );
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepContainer>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
              Configure Your Meal Plan
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Plan Name"
                  value={planConfig.planName}
                  onChange={(e) =>
                    setPlanConfig({ ...planConfig, planName: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Number of Days</InputLabel>
                  <Select
                    value={planConfig.days}
                    onChange={(e) =>
                      setPlanConfig({ ...planConfig, days: e.target.value })
                    }
                    label="Number of Days"
                  >
                    <MenuItem value={3}>3 Days</MenuItem>
                    <MenuItem value={5}>5 Days (Weekdays)</MenuItem>
                    <MenuItem value={7}>7 Days (Full Week)</MenuItem>
                    <MenuItem value={14}>14 Days (2 Weeks)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Meals Per Day</InputLabel>
                  <Select
                    value={planConfig.mealsPerDay}
                    onChange={(e) =>
                      setPlanConfig({
                        ...planConfig,
                        mealsPerDay: e.target.value,
                      })
                    }
                    label="Meals Per Day"
                  >
                    <MenuItem value={2}>2 Meals (Breakfast + Dinner)</MenuItem>
                    <MenuItem value={3}>
                      3 Meals (Breakfast + Lunch + Dinner)
                    </MenuItem>
                    <MenuItem value={4}>4 Meals (+ Snack)</MenuItem>
                    <MenuItem value={5}>5 Meals (+ 2 Snacks)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>
                  Daily Calories: {planConfig.caloriesPerDay}
                </Typography>
                <Slider
                  value={planConfig.caloriesPerDay}
                  onChange={(e, value) =>
                    setPlanConfig({ ...planConfig, caloriesPerDay: value })
                  }
                  min={1200}
                  max={4000}
                  step={50}
                  marks={[
                    { value: 1200, label: "1200" },
                    { value: 2000, label: "2000" },
                    { value: 3000, label: "3000" },
                    { value: 4000, label: "4000" },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <GenerateButton
                onClick={generateMealPlan}
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <AIIcon />
                }
              >
                {loading ? "Generating..." : "Generate AI Meal Plan"}
              </GenerateButton>
            </Box>
          </StepContainer>
        );

      case 1:
        return (
          <StepContainer>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
              Your AI-Generated Meal Plan
            </Typography>

            {generatedPlan && (
              <Box>
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Plan Summary
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Duration:</strong> {generatedPlan.days} days •
                    <strong> Meals per day:</strong>{" "}
                    {generatedPlan.meals_per_day} •
                    <strong> Target calories:</strong>{" "}
                    {generatedPlan.target_calories}/day
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {Array.from({ length: generatedPlan.days }, (_, dayIndex) => (
                    <Grid item xs={12} key={dayIndex}>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, fontWeight: "bold" }}
                      >
                        Day {dayIndex + 1}
                      </Typography>
                      <Grid container spacing={2}>
                        {generatedPlan.meals
                          .filter((meal) => meal.day === dayIndex + 1)
                          .map((meal, mealIndex) => (
                            <Grid item xs={12} sm={6} md={4} key={mealIndex}>
                              <MealCard>
                                <CardContent>
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "bold", mb: 1 }}
                                  >
                                    {meal.meal_type}
                                  </Typography>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ mb: 1 }}
                                  >
                                    {meal.recipe_name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 2 }}
                                  >
                                    {meal.calories} calories • {meal.prep_time}{" "}
                                    min prep
                                  </Typography>

                                  <Accordion>
                                    <AccordionSummary
                                      expandIcon={<ExpandMoreIcon />}
                                    >
                                      <Typography variant="body2">
                                        View Details
                                      </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ mb: 1 }}
                                      >
                                        Ingredients:
                                      </Typography>
                                      <List dense>
                                        {meal.ingredients?.map(
                                          (ingredient, idx) => (
                                            <ListItem key={idx} sx={{ py: 0 }}>
                                              <ListItemText
                                                primary={ingredient}
                                              />
                                            </ListItem>
                                          )
                                        )}
                                      </List>

                                      <Typography
                                        variant="subtitle2"
                                        sx={{ mt: 2, mb: 1 }}
                                      >
                                        Instructions:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        {meal.instructions}
                                      </Typography>

                                      <Typography
                                        variant="subtitle2"
                                        sx={{ mt: 2, mb: 1 }}
                                      >
                                        Nutrition:
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <Chip
                                          label={`Protein: ${meal.nutrition?.protein}g`}
                                          size="small"
                                        />
                                        <Chip
                                          label={`Carbs: ${meal.nutrition?.carbs}g`}
                                          size="small"
                                        />
                                        <Chip
                                          label={`Fat: ${meal.nutrition?.fat}g`}
                                          size="small"
                                        />
                                        <Chip
                                          label={`Fiber: ${meal.nutrition?.fiber}g`}
                                          size="small"
                                        />
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                </CardContent>
                              </MealCard>
                            </Grid>
                          ))}
                      </Grid>
                      {dayIndex < generatedPlan.days - 1 && (
                        <Divider sx={{ my: 3 }} />
                      )}
                    </Grid>
                  ))}
                </Grid>

                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    gap: 2,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={generateMealPlan}
                    disabled={loading}
                    startIcon={<RefreshIcon />}
                  >
                    Regenerate Plan
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    startIcon={<ScheduleIcon />}
                  >
                    Continue to Schedule
                  </Button>
                </Box>
              </Box>
            )}
          </StepContainer>
        );

      case 2:
        return (
          <StepContainer>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
              Schedule & Save Your Plan
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, display: "flex", alignItems: "center" }}
                    >
                      <SaveIcon sx={{ mr: 1 }} /> Save Meal Plan
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 2 }}
                    >
                      Save this meal plan to your account for easy access and
                      tracking.
                    </Typography>
                    <TextField
                      label="Plan Name"
                      value={planConfig.planName}
                      onChange={(e) =>
                        setPlanConfig({
                          ...planConfig,
                          planName: e.target.value,
                        })
                      }
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="contained"
                      onClick={saveMealPlan}
                      disabled={loading}
                      startIcon={
                        loading ? <CircularProgress size={16} /> : <SaveIcon />
                      }
                      fullWidth
                    >
                      {loading ? "Saving..." : "Save Meal Plan"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, display: "flex", alignItems: "center" }}
                    >
                      <CartIcon sx={{ mr: 1 }} /> Grocery List
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 2 }}
                    >
                      Automatically add all ingredients to your grocery cart for
                      easy shopping.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="outlined"
                      onClick={addToGroceryList}
                      disabled={loading}
                      startIcon={<CartIcon />}
                      fullWidth
                    >
                      Add to Grocery Cart
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>

            <Box
              sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}
            >
              <Button onClick={handleBack}>Back</Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!success.includes("saved")}
              >
                Finish
              </Button>
            </Box>
          </StepContainer>
        );

      case 3:
        return (
          <StepContainer>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography
                variant="h5"
                sx={{ mb: 2, fontWeight: "bold", color: "success.main" }}
              >
                🎉 Meal Plan Created Successfully!
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                Your personalized meal plan has been saved and is ready to use.
                You can now schedule your meals and start cooking!
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(0)}
                  startIcon={<RefreshIcon />}
                >
                  Create Another Plan
                </Button>
                <Button variant="contained" href="/app/home">
                  Go to Dashboard
                </Button>
              </Box>
            </Box>
          </StepContainer>
        );

      default:
        return "Unknown step";
    }
  };

  return (
    <GeneratorContainer elevation={2}>
      <Typography
        variant="h4"
        align="center"
        sx={{ mb: 4, fontWeight: "bold", fontFamily: "Montserrat" }}
      >
        Generate Your Personalized Meal Plan
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
            <StepContent>{getStepContent(index)}</StepContent>
          </Step>
        ))}
      </Stepper>
    </GeneratorContainer>
  );
});

export default MealPlanGenerator;
